import { useState } from 'react';
import { IntegrationDefinition } from '@/types/integrations';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';

interface StandardConnectionDialogProps {
  integration: IntegrationDefinition | null;
  open: boolean;
  onClose: () => void;
  onConnect: (integrationId: string, credentials: Record<string, string>) => Promise<void>;
}

export function StandardConnectionDialog({
  integration,
  open,
  onClose,
  onConnect,
}: StandardConnectionDialogProps) {
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [testing, setTesting] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const handleClose = () => {
    setCredentials({});
    setTesting(false);
    setConnecting(false);
    setTestResult(null);
    onClose();
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Mock test result
    const success = Math.random() > 0.3;
    setTestResult({
      success,
      message: success ? 'Connection successful!' : 'Connection failed. Please check your credentials.',
    });
    setTesting(false);
  };

  const handleConnect = async () => {
    if (!integration) return;
    
    setConnecting(true);
    try {
      await onConnect(integration.id, credentials);
      handleClose();
    } catch (error) {
      console.error('Connection failed:', error);
    } finally {
      setConnecting(false);
    }
  };

  const isFormValid = () => {
    if (!integration?.configFields) return false;
    return integration.configFields
      .filter(field => field.required)
      .every(field => credentials[field.key]?.trim());
  };

  if (!integration) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="text-3xl">{integration.logo}</div>
            <DialogTitle>{integration.name}</DialogTitle>
          </div>
          <DialogDescription>{integration.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {integration.configFields?.map((field) => (
            <div key={field.key} className="space-y-2">
              <Label htmlFor={field.key}>
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </Label>
              {field.type === 'select' ? (
                <Select
                  value={credentials[field.key] || ''}
                  onValueChange={(value) =>
                    setCredentials({ ...credentials, [field.key]: value })
                  }
                >
                  <SelectTrigger id={field.key}>
                    <SelectValue placeholder={`Select ${field.label}`} />
                  </SelectTrigger>
                  <SelectContent>
                    {field.options?.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id={field.key}
                  type={field.type}
                  placeholder={field.placeholder}
                  value={credentials[field.key] || ''}
                  onChange={(e) =>
                    setCredentials({ ...credentials, [field.key]: e.target.value })
                  }
                />
              )}
            </div>
          ))}

          {testResult && (
            <div
              className={`p-3 rounded-md text-sm ${
                testResult.success
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}
            >
              {testResult.message}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleTestConnection}
            disabled={!isFormValid() || testing || connecting}
          >
            {testing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Testing...
              </>
            ) : (
              'Test Connection'
            )}
          </Button>
          <Button variant="outline" onClick={handleClose} disabled={connecting}>
            Cancel
          </Button>
          <Button
            onClick={handleConnect}
            disabled={!isFormValid() || connecting}
          >
            {connecting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Connecting...
              </>
            ) : (
              'Connect'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
